/**
 * Service Registry - Phase 4 Architecture Modernization
 * Implements centralized service management with dependency injection
 * Based on architectural-consolidation-plan.md
 */

import { logger } from '../lib/logger';

// Re-export canonical types and helpers from @flowstate/types
export type {
  IService,
  ServiceError,
  ServiceResponse,
  ServiceResponseSuccess,
  ServiceResponseError,
  ServiceResponseMetadata,
  ServiceConfig,
  CacheStats,
  PerformanceMetrics,
} from '@flowstate/types';

export {
  createServiceResponse,
} from '@flowstate/types';



// Import for use in this file
import type {
  IService,
  ServiceConfig,
  ServiceError,
  ServiceResponseSuccess,
  ServiceResponseError,
  ServiceResponseMetadata,
} from '@flowstate/types';

// Service factory type — factories may be async
type ServiceFactory<T = IService> = () => T | Promise<T>;

/**
 * Centralized Service Registry
 * Manages service lifecycle and dependencies
 */
export class ServiceRegistry {
  private services = new Map<string, IService>();
  private factories = new Map<string, ServiceFactory>();
  private configs = new Map<string, ServiceConfig>();
  private initialized = new Set<string>();

  constructor() {
    logger.info("🏗️ ServiceRegistry initialized");
  }

  /**
   * Register a service factory with optional configuration
   */
  register<T extends IService>(
    name: string,
    factory: ServiceFactory<T>,
    config?: ServiceConfig
  ): void {
    this.factories.set(name, factory);
    if (config) {
      this.configs.set(name, config);
    }
    logger.info(`📝 Service registered: ${name}`);
  }

  /**
   * Get a service instance (lazy loading)
   */
  async get<T extends IService>(name: string): Promise<T> {
    // Return cached instance if available
    if (this.services.has(name)) {
      return this.services.get(name) as T;
    }

    // Create new instance
    const factory = this.factories.get(name);
    if (!factory) {
      throw new Error(`Service '${name}' not registered`);
    }

    const startTime = Date.now();
    try {
      const service = await factory() as T;

      // Initialize if not already done
      // Check if service is already initialized (e.g., by another registry pass)
      if (!this.initialized.has(name) && service.initialize) {
        try {
          await service.initialize();
          this.initialized.add(name);
        } catch (error) {
          // If initialization fails because it's already initialized, mark it as initialized
          if (error instanceof Error && error.message.includes('already')) {
            this.initialized.add(name);
          } else {
            throw error;
          }
        }
      } else if (!this.initialized.has(name) && !service.initialize) {
        // If service doesn't have initialize method, mark it as initialized
        this.initialized.add(name);
      }

      // Cache the instance
      this.services.set(name, service);

      const duration = Date.now() - startTime;
      logger.info(`🚀 Service loaded: ${name} (${duration}ms)`);

      return service;
    } catch (error) {
      logger.error(`❌ Failed to load service: ${name}`, { error });
      throw error;
    }
  }

  /**
   * Synchronously get a service (must be already initialized)
   * If service is registered but not in cache, create it synchronously
   * Note: This assumes the service instance is a singleton and may have been initialized elsewhere
   */
  getSync<T extends IService>(name: string): T {
    // Check if already cached
    if (this.services.has(name)) {
      // If it's cached, it's considered initialized
      this.initialized.add(name);
      return this.services.get(name) as T;
    }

    // If registered but not cached, create it now
    const factory = this.factories.get(name);
    if (factory) {
      const serviceOrPromise = factory();
      // Check if it's a promise (async factory)
      if (serviceOrPromise instanceof Promise) {
        throw new Error(`Service '${name}' uses async factory. Use get() instead of getSync().`);
      }
      const service = serviceOrPromise as T;
      // Cache the instance
      this.services.set(name, service);
      // Mark as initialized since getSync is for already-initialized services
      // If the service was initialized via direct calls (e.g., initializeCortexServices),
      // this ensures the registry knows about it
      this.initialized.add(name);
      return service;
    }

    throw new Error(`Service '${name}' not initialized. Use get() instead.`);
  }

  /**
   * Check if a service is registered
   */
  has(name: string): boolean {
    return this.factories.has(name);
  }

  /**
   * Check if a service is initialized
   */
  isInitialized(name: string): boolean {
    return this.initialized.has(name);
  }

  /**
   * Get service configuration
   */
  getConfig(name: string): ServiceConfig | undefined {
    return this.configs.get(name);
  }

  /**
   * Update service configuration
   */
  updateConfig(name: string, config: Partial<ServiceConfig>): void {
    const existing = this.configs.get(name) || { features: {}, limits: {} };
    this.configs.set(name, { ...existing, ...config });
  }

  /**
   * Dispose all services (cleanup)
   */
  async dispose(): Promise<void> {
    logger.info("🧹 Disposing all services...");

    for (const [name, service] of this.services.entries()) {
      try {
        if (service.dispose) {
          await service.dispose();
        }
        logger.info(`✅ Service disposed: ${name}`);
      } catch (error) {
        logger.error(`❌ Error disposing service: ${name}`, { error });
      }
    }

    this.services.clear();
    this.initialized.clear();
    logger.info("🎯 All services disposed");
  }

  /**
   * Get registry statistics
   */
  getStats(): {
    registered: number;
    initialized: number;
    services: string[];
  } {
    return {
      registered: this.factories.size,
      initialized: this.initialized.size,
      services: Array.from(this.factories.keys()),
    };
  }

  /**
   * Dependency resolution helper
   */
  async resolveDependencies<T extends IService>(
    name: string,
    dependencies: string[]
  ): Promise<T> {
    // Initialize dependencies first
    await Promise.all(dependencies.map((dep) => this.get(dep)));

    // Then get the requested service
    return this.get<T>(name);
  }
}

/**
 * Global service registry instance
 */
export const serviceRegistry = new ServiceRegistry();

/**
 * Service registration utilities
 */
export function registerService<T extends IService>(
  name: string,
  factory: ServiceFactory<T>,
  config?: ServiceConfig
): void {
  serviceRegistry.register(name, factory, config);
}

export function getService<T extends IService>(name: string): Promise<T> {
  return serviceRegistry.get<T>(name);
}

/**
 * Environment-based service configuration
 */
export function getServiceConfig(_service: string): ServiceConfig {
  // Vite exposes env variables on import.meta.env in the browser, while Node uses process.env
  const env: Record<string, string | undefined> =
    typeof process !== "undefined" && process.env
      ? process.env
      : typeof import.meta !== "undefined"
        ? ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {})
        : {};

  const isProduction =
    env.NODE_ENV === "production" || env.MODE === "production";
  const isDevelopment =
    env.NODE_ENV === "development" || env.MODE === "development";

  return {
    features: {
      caching: isProduction,
      monitoring: env.ENABLE_MONITORING === "true" || isDevelopment,
      rateLimiting: isProduction,
      logging: isDevelopment,
      metrics: isProduction,
    },
    limits: {
      maxRetries: 3,
      timeout: 30000,
      cacheTime: isProduction ? 300000 : 60000, // 5min prod, 1min dev
      maxCacheSize: 100,
      rateLimitWindow: 60000, // 1 minute
      rateLimitMax: 100,
    },
    endpoints: {
      // Populated by individual services
    },
    credentials: {
      // Populated by individual services
    },
  };
}

// Enhanced error handling for services
export function createServiceError(
  code: string,
  message: string,
  service?: string,
  context?: Record<string, unknown>
): ServiceError {
  return {
    code,
    message,
    service,
    timestamp: new Date(),
    context,
  };
}

/**
 * Create an error service response (modern pattern).
 * Accepts a plain message or a ServiceError; superset of the canonical
 * `createErrorResponse` from @flowstate/types.
 */
export function createErrorResponse<T = unknown>(
  error: string | ServiceError
): ServiceResponseError<T> {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return {
      success: false,
      error: error.message || 'Unknown service error',
      code: error.code,
      service: error.service,
      context: error.context as Record<string, unknown>
    } as ServiceResponseError<T>;
  }
  return {
    success: false,
    error: typeof error === 'string' ? error : 'Unknown error',
  } as ServiceResponseError<T>;
}

/**
 * Create a success service response (modern pattern)
 */
export function createSuccessResponse<T>(
  data: T,
  metadata?: ServiceResponseMetadata
): ServiceResponseSuccess<T> {
  return {
    success: true,
    data,
    metadata,
  };
}
